import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled, { css } from 'styled-components';

const POST_REVEAL_DELAY_MS = 100;

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(${p => p.size}, 1fr);
  gap: 4px;
  max-width: 640px;
  margin: 0 auto;
  aspect-ratio: 1 / 1;

  @media (max-width: ${p => p.theme.breakpoints.tablet}px) {
    max-width: 100%;
    gap: 3px;
  }

  @media (max-width: ${p => p.theme.breakpoints.mobile}px) {
    gap: 2px;
  }
`;

const cellStateStyles = {
  idle: css`
    background: ${p => p.palette.cellBg};
    border: 1px solid ${p => p.palette.cellBorder};
  `,
  tapped: css`
    background: ${p => p.palette.tappedBg};
    border: 2px solid ${p => p.palette.tappedBorder};
    box-shadow: 0 0 12px ${p => p.palette.pathGlow};
    transform: scale(1.05);
  `,
  path: css`
    background: ${p => p.palette.tappedBg};
    border: 2px solid ${p => p.palette.tappedBorder};
    box-shadow: 0 0 6px ${p => p.palette.pathGlow};
    transform: scale(1.02);
  `,
  dimmed: css`
    background: ${p => p.palette.cellBg};
    border: 1px solid ${p => p.palette.cellBorder};
    opacity: 0.4;
  `,
};

const Cell = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  aspect-ratio: 1 / 1;
  padding: 0;
  cursor: ${p => p.disabled ? 'default' : 'pointer'};
  border-radius: 6px;
  transition: all 0.25s ease;
  font-family: 'Noto Sans Devanagari', serif;
  min-height: 40px;

  ${p => cellStateStyles[p.state] || cellStateStyles.idle}

  &:hover:not(:disabled) {
    border-color: ${p => p.palette.tappedBorder};
    transform: scale(1.03);
  }

  &:focus-visible {
    outline: 2px solid ${p => p.palette.tappedBorder};
    outline-offset: 2px;
  }
`;

const Primary = styled.span`
  font-size: 20px;
  font-weight: 600;
  color: ${p => p.palette.primaryColor};
  line-height: 1;

  @media (max-width: ${p => p.theme.breakpoints.mobile}px) {
    font-size: 16px;
  }
`;

const Secondary = styled.span`
  font-size: 10px;
  color: ${p => p.palette.secondaryColor};
  font-family: 'Noto Sans Tamil', sans-serif;
  margin-top: 2px;
  line-height: 1;
`;

function prefersReducedMotion() {
  return typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
}

const PathTracingGrid = ({
  size,
  cells,
  palette,
  pathForCell,
  stepDurationMs = 300,
  onPathComplete,
  ariaLabel,
}) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [startCellIndex, setStartCellIndex] = useState(null);
  const [illuminatedCount, setIlluminatedCount] = useState(0);
  const timeoutsRef = useRef([]);
  const pathRef = useRef(null);

  const handleCellTap = useCallback((cellIndex) => {
    if (isAnimating) return;
    const path = pathForCell(cellIndex);
    pathRef.current = path;
    setIsAnimating(true);
    setStartCellIndex(cellIndex);
    setIlluminatedCount(1);

    if (prefersReducedMotion()) {
      setIlluminatedCount(size);
      timeoutsRef.current.push(setTimeout(() => {
        onPathComplete(cellIndex);
      }, POST_REVEAL_DELAY_MS));
      return;
    }

    for (let step = 2; step <= size; step++) {
      timeoutsRef.current.push(setTimeout(() => {
        setIlluminatedCount(step);
      }, (step - 1) * stepDurationMs));
    }
    timeoutsRef.current.push(setTimeout(() => {
      onPathComplete(cellIndex);
    }, (size - 1) * stepDurationMs + POST_REVEAL_DELAY_MS));
  }, [isAnimating, onPathComplete, pathForCell, size, stepDurationMs]);

  useEffect(() => {
    const timeouts = timeoutsRef.current;
    return () => {
      timeouts.forEach(clearTimeout);
      timeoutsRef.current = [];
    };
  }, []);

  const computeCellState = (cellIndex) => {
    if (!isAnimating || startCellIndex === null) return 'idle';
    const path = pathRef.current;
    if (!path) return 'idle';
    if (cellIndex === startCellIndex && illuminatedCount >= 1) return 'tapped';
    const pos = path.indexOf(cellIndex);
    if (pos >= 1 && illuminatedCount > pos) return 'path';
    if (pos === -1) return 'dimmed';
    return 'idle';
  };

  const totalCells = size * size;

  return (
    <GridContainer role="grid" aria-label={ariaLabel} size={size}>
      {cells.map((c, idx) => {
        const state = computeCellState(idx);
        const nonInteractive = state === 'dimmed' || state === 'path' || state === 'tapped';
        return (
          <Cell
            key={idx}
            role="gridcell"
            aria-label={`letter ${c.primary}, cell ${idx + 1} of ${totalCells}`}
            state={state}
            palette={palette}
            onClick={() => handleCellTap(idx)}
            disabled={isAnimating || nonInteractive}
            tabIndex={state === 'idle' && !isAnimating ? 0 : -1}
          >
            <Primary palette={palette}>{c.primary}</Primary>
            {c.secondary && <Secondary palette={palette}>{c.secondary}</Secondary>}
          </Cell>
        );
      })}
    </GridContainer>
  );
};

export default PathTracingGrid;
